"use client";

import { useHydrated } from "@trydig/ui/hooks";
import { withSearchParams } from "@trydig/ui/next";
import { type FormEvent, useMemo, useRef, useState } from "react";
import { SearchIcon } from "@/components/icons/Search";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Input";
import { ACTIVE_RETAILER_KEY } from "@/components/ui/Mapbox/config";
import { Skeleton } from "@/components/ui/Skeleton";
import { useTranslations } from "@/i18n/client";
import type { BlokRetailer } from "@/storyblok";
import { Retailer, RetailerSkeleton } from "../../Retailer";
import { RetailersMap, RetailersMapSkeleton } from "./RetailersMap";
import styles from "./RetailersMap.module.css";

export interface RetailersMapLoaderProps {
	retailers: BlokRetailer[];
	mapZoom: number;
	mapCenter: [number, number];
}

export const RetailersMapLoader = withSearchParams<RetailersMapLoaderProps>(
	({ retailers, searchParams, mapCenter, mapZoom }) => {
		const hydrated = useHydrated();
		const activeRetailer = searchParams.get(ACTIVE_RETAILER_KEY);
		const t = useTranslations();
		const mapRef = useRef<mapboxgl.Map | null>(null);
		const sentinelRef = useRef<HTMLDivElement | null>(null);
		// const page = Number(searchParams.get(RETAILER_PAGE_SEARCH_PARAM)) || 1;
		const [inputValue, _setInputValue] = useState<string>(
			searchParams.get("q") ?? "",
		);

		const handleSearch = (event: FormEvent) => {
			event.preventDefault();

			const formData = new FormData(event.target as HTMLFormElement);
			const query = formData.get("q") as string;

			if (!query) {
				return searchParams.delete("q");
			}
			searchParams.set("q", query);
		};

		const filteredRetailers = useMemo(() => {
			const query = searchParams.get("q");

			return retailers.filter((retailer) => {
				const matchesQuery = query
					? new RegExp(query, "i").test(retailer.name) ||
						new RegExp(query, "i").test(retailer.street ?? "") ||
						new RegExp(query, "i").test(retailer.city as string) ||
						new RegExp(query, "i").test(retailer.postalCode as string) ||
						new RegExp(query, "i").test(retailer.phone as string) ||
						new RegExp(query, "i").test(retailer.country as string)
					: true;

				return matchesQuery;
			});
		}, [retailers, searchParams]);

		// const pages = Math.ceil(filteredRetailers.length / RETAILERS_PER_PAGE);
		// const sliceStart = (page - 1) * RETAILERS_PER_PAGE;
		// const sliceEnd = page * RETAILERS_PER_PAGE;

		// const slicedRetailers = filteredRetailers.slice(sliceStart, sliceEnd);

		const scrollToTop = () => {
			if (sentinelRef.current) {
				const elementPosition =
					sentinelRef.current.getBoundingClientRect().top + window.scrollY;
				const navHeight = getComputedStyle(
					document.documentElement,
				).getPropertyValue("--navbar-height");

				const offsetPosition =
					elementPosition - Number(navHeight.replace("px", ""));

				window.scrollTo({
					top: offsetPosition,
					behavior: "smooth",
				});
			}
		};

		if (!hydrated) return <RetailersMapLoaderSkeleton />;

		return (
			<>
				<div ref={sentinelRef} />
				<div className={styles.content}>
					<form className={styles.search} onSubmit={handleSearch}>
						<TextField
							name="q"
							className={styles.searchInput}
							defaultValue={inputValue}
							placeholder={t("Type a postcode or address")}
							onChange={(e) => {
								if (!e.target.value) {
									searchParams.delete("q");
								}
							}}
						/>
						<Button
							aria-label="Search for retailers"
							type="submit"
							variant="community"
							className={styles.searchButton}
						>
							<SearchIcon />
						</Button>
					</form>

					<RetailersMap
						retailers={filteredRetailers}
						mapZoom={mapZoom}
						mapCenter={mapCenter}
						ref={mapRef}
					/>

					<span className={styles.text}>
						{t(
							"Can't find your favorite retailer on the list? Please let us know! We are currently expanding our global distribution.",
						)}
					</span>
				</div>

				<div className={styles.retailers}>
					{filteredRetailers.map((retailer, i) => (
						<Retailer
							key={i}
							onClick={() => {
								scrollToTop();
								mapRef.current?.flyTo({
									center: [
										Number(retailer.longitude),
										Number(retailer.latitude),
									],
									zoom: mapZoom * 1.5,
								});

								searchParams.set(ACTIVE_RETAILER_KEY, retailer.name);
							}}
							name={retailer.name}
							street={retailer.street}
							city={retailer.city}
							country={retailer.country}
							postalCode={retailer.postalCode}
							website={retailer.website}
							phone={retailer.phone}
							isActive={activeRetailer === retailer.name}
							coordinates={[
								Number(retailer.latitude),
								Number(retailer.longitude),
							]}
						/>
					))}
				</div>
			</>
		);
	},
);

export const RetailersMapLoaderSkeleton = ({
	retailersSzie = 6,
}: {
	retailersSzie?: number;
}) => {
	return (
		<>
			<div className={styles.content}>
				<Skeleton>
					<form className={styles.search}>
						<TextField name="q" className={styles.searchInput} />
						<Button
							type="submit"
							variant="community"
							className={styles.searchButton}
						>
							<SearchIcon />
						</Button>
					</form>
				</Skeleton>

				<RetailersMapSkeleton />

				<Skeleton fitContent>
					<span className={styles.text}>
						Lorem ipsum dolor sit amet consectetur adipisicing elit. Optio,
						ipsum.
					</span>
				</Skeleton>
			</div>
			<div className={styles.retailers}>
				{Array.from({ length: retailersSzie }).map((_, i) => (
					<RetailerSkeleton key={i} />
				))}
			</div>
		</>
	);
};
